import { useCallback, useEffect, useRef } from "react";
import { useFetcher, useNavigate, useRevalidator } from "react-router";
import { toast } from "sonner";
import {
	WORKSPACE_SIDEBAR_ACTION_PATH,
	type WorkspaceSidebarActionIntent,
	type WorkspaceSidebarActionResult,
	type WorkspaceSidebarActionSuccess,
	workspaceSidebarAction,
} from "@/routes/workspaceSidebarAction";

type SubmitOptions = {
	onSuccess?: (result: WorkspaceSidebarActionSuccess) => void;
};

type SuccessCallbackEntry = {
	intent: WorkspaceSidebarActionIntent;
	callback: (result: WorkspaceSidebarActionSuccess) => void;
};

const buildFormData = (
	intent: WorkspaceSidebarActionIntent,
	fields?: Record<string, string>,
) => {
	const formData = new FormData();
	formData.set("intent", intent);
	if (fields) {
		Object.entries(fields).forEach(([key, value]) => {
			if (typeof value === "string") {
				formData.set(key, value);
			}
		});
	}
	return formData;
};

export const useWorkspaceSidebarActions = () => {
	const fetcher = useFetcher<typeof workspaceSidebarAction>();
	const revalidator = useRevalidator();
	const navigate = useNavigate();
	const handledResult = useRef<WorkspaceSidebarActionResult | null>(null);
	const successCallbacks = useRef<SuccessCallbackEntry[]>([]);

	const submit = useCallback(
		(
			intent: WorkspaceSidebarActionIntent,
			fields?: Record<string, string>,
			options?: SubmitOptions,
		) => {
			const formData = buildFormData(intent, fields);
			if (options?.onSuccess) {
				successCallbacks.current.push({ intent, callback: options.onSuccess });
			}
			fetcher.submit(formData, {
				method: "post",
				action: WORKSPACE_SIDEBAR_ACTION_PATH,
			});
		},
		[fetcher],
	);

	useEffect(() => {
		if (fetcher.state !== "idle" || !fetcher.data) {
			return;
		}

		const result = fetcher.data;

		if (handledResult.current === result) {
			return;
		}

		handledResult.current = result;

		if (result.ok) {
			revalidator.revalidate();
			if (result.navigateTo) {
				navigate(result.navigateTo);
			}
			const callbackIndex = successCallbacks.current.findIndex(
				(entry) => entry.intent === result.intent,
			);
			if (callbackIndex !== -1) {
				const [entry] = successCallbacks.current.splice(callbackIndex, 1);
				try {
					entry.callback(result);
				} catch (error) {
					console.error(
						"[useWorkspaceSidebarActions] post-submit callback failed",
						error,
					);
				}
			}
		} else if (result.error) {
			successCallbacks.current = successCallbacks.current.filter(
				(entry) => entry.intent !== result.intent,
			);
			toast.error(result.error);
		}
	}, [fetcher.state, fetcher.data, navigate, revalidator]);

	const createProject = useCallback(
		(fields?: Record<string, string>, options?: SubmitOptions) =>
			submit("createProject", fields, options),
		[submit],
	);
	const createJudge = useCallback(
		(fields?: Record<string, string>, options?: SubmitOptions) =>
			submit("createJudge", fields, options),
		[submit],
	);
	const createCode = useCallback(
		(fields?: Record<string, string>, options?: SubmitOptions) =>
			submit("createCode", fields, options),
		[submit],
	);
	const createDataset = useCallback(
		(fields?: Record<string, string>, options?: SubmitOptions) =>
			submit("createDataset", fields, options),
		[submit],
	);

	const updateProjectTitle = useCallback(
		(entityId: string, title: string, options?: SubmitOptions) =>
			submit("updateProjectTitle", { entityId, title }, options),
		[submit],
	);
	const toggleProjectPublic = useCallback(
		(entityId: string, isPublic: boolean, options?: SubmitOptions) =>
			submit("projectTogglePublic", { entityId, isPublic: String(isPublic) }, options),
		[submit],
	);
	const updateJudgeTitle = useCallback(
		(entityId: string, title: string, options?: SubmitOptions) =>
			submit("updateJudgeTitle", { entityId, title }, options),
		[submit],
	);
	const updateCodeTitle = useCallback(
		(entityId: string, title: string, options?: SubmitOptions) =>
			submit("updateCodeTitle", { entityId, title }, options),
		[submit],
	);

	const assignBehaviorDataset = useCallback(
		(entityId: string, datasetId: string | null, options?: SubmitOptions) =>
			submit(
				"projectAssignBehaviorDataset",
				{
					entityId,
					datasetId: datasetId ?? "none",
				},
				options,
			),
		[submit],
	);

	const assignRulesDataset = useCallback(
		(entityId: string, datasetId: string | null, options?: SubmitOptions) =>
			submit(
				"projectAssignRulesDataset",
				{
					entityId,
					datasetId: datasetId ?? "none",
				},
				options,
			),
		[submit],
	);

	const assignConfiguration = useCallback(
		(
			entityId: string,
			agentId: string | null,
			codeId: string | null,
			options?: SubmitOptions,
		) => {
			// Enforce mutual exclusivity at the client level
			if (agentId && codeId) {
				console.error("Cannot assign both agent and code");
				return;
			}

			submit(
				"projectAssignConfiguration",
				{
					entityId,
					agentId: agentId ?? "none",
					codeId: codeId ?? "none",
				},
				options,
			);
		},
		[submit],
	);

	const deleteProject = useCallback(
		(entityId: string, options?: SubmitOptions) =>
			submit("projectDelete", { entityId }, options),
		[submit],
	);

	const isPending = useCallback(
		(intent: WorkspaceSidebarActionIntent) =>
			fetcher.state !== "idle" && fetcher.formData?.get("intent") === intent,
		[fetcher.state, fetcher.formData],
	);

	return {
		createProject,
		createJudge,
		createCode,
		createDataset,
		updateProjectTitle,
		toggleProjectPublic,
		updateJudgeTitle,
		updateCodeTitle,
		assignBehaviorDataset,
		assignRulesDataset,
		assignConfiguration,
		deleteProject,
		isPending,
	} as const;
};
